/**
 * "Suggestions": which patterns the tiles a player holds are closest to (Indra, 2026-10-04) — the Hand helper in Fun and Serious games.
 *
 * For every structural pattern it works out the fewest MORE tiles needed to complete a winning hand that has that pattern. "Needed" is
 * counted from the tiles entered, so with 8 entered the other 5 count as absent, and with all 13 entered each loose tile can seed a set —
 * which is why the answer improves as more of the hand is entered. It sees only the player's own tiles: it cannot know what has been
 * discarded, which is why it is only ever a suggestion.
 *
 * Only what the tiles alone decide is counted. Self-draw, a fully concealed hand, flowers, seat/round wind, and every "circumstance"
 * (last tile, robbing a kong ...) cannot be known at the start of a round, so they are left out and each suggestion shows the pattern's
 * OWN fan — exactly what the end-of-round preview later adds to.
 *
 * Pure: no I/O, no clock, no DOM. The DP is tiny (states are position x slots x three tile counts), so it is cheap enough to run on every tap.
 */

import { rankOf, suitOf, type Tile } from '../tiles';
import type { HeldKong, Suggestion, Suggestions } from '../types';
import { fixedLimitFan } from './patterns';
import { activeFanCap, type HkosSettings } from './settings';

/** Nothing is suggested until this many tiles are entered. */
export const SUGGEST_MIN_TILES = 8;
/** At most this many regular suggestions, and this many "Want to Win Big?" ones. */
export const SUGGEST_MAX_REGULAR = 5;
export const SUGGEST_MAX_BIG = 3;
/** A pattern needing more tiles than this is not worth mentioning. */
export const SUGGEST_REGULAR_WITHIN = 7;
export const SUGGEST_BIG_WITHIN = 6;

/**
 * When a pattern is worth mentioning at all, in terms of what is ALREADY in hand (Indra, 2026-10-04 — a suggestion the tiles do not
 * already point to is not useful). Beyond the "within N tiles" cut-off, these four-ish rules decide it:
 *  - Seven Pairs: at least 4 pairs (four identical tiles count as two pairs, as the scoring does).
 *  - All Pungs: at least 3 groups of matching tiles — three pairs, or a pung and two pairs, or more pungs (a declared kong is a pung).
 *  - Half Flush: at least 7 tiles of one suit and at least 1 honour tile.
 *  - Full Flush: at least 8 tiles of one suit and no honour tile at all.
 *  - Dragon Pung: a pair of the same dragon (or a declared dragon kong).
 *  - All Sequences: at least 3 runs — a full sequence, or two neighbouring tiles of one suit, each using its own tiles.
 *  - Mixed Orphans: at least 6 tiles that are 1s, 9s or honours.
 *  - Small Dragons: two different dragons, each held at least twice.
 *  - Great Dragons: a pung of one dragon and a pair of another.
 *  - Small Winds: at least 6 wind tiles.
 *  And for the limit hands ("Want to Win Big?"):
 *  - Thirteen Orphans: at least 8 different terminals and honours.
 *  - Seven Shifted Pairs: pairs of 4 consecutive ranks of one suit.
 *  - Great Winds: at least 8 wind tiles.
 *  - All Kongs: at least 2 declared kongs.
 *  - All Honours: at least 8 honour tiles.
 *  - Orphans: at least 8 terminals (1s and 9s).
 *  - Nine Gates: at least 8 tiles of one suit, a pair (or more) of one of its terminals (1 or 9) and at least one tile of the other terminal.
 *  - Self Triplets: as All Pungs (at least 3 groups), and no claimed kong.
 */
export const SEVEN_PAIRS_MIN_PAIRS = 4;
export const ALL_PUNGS_MIN_GROUPS = 3;
export const HALF_FLUSH_MIN_SUIT_TILES = 7;
export const HALF_FLUSH_MIN_HONOURS = 1;
export const FULL_FLUSH_MIN_SUIT_TILES = 8;
export const ALL_SEQUENCES_MIN_RUNS = 3;
export const MIXED_ORPHANS_MIN_TILES = 6;
export const SMALL_WINDS_MIN_WIND_TILES = 6;
export const GREAT_WINDS_MIN_WIND_TILES = 8;
export const THIRTEEN_ORPHANS_MIN_TYPES = 8;
export const SEVEN_SHIFTED_MIN_RUN = 4;
export const ALL_KONGS_MIN_KONGS = 2;
export const ALL_HONOURS_MIN_TILES = 8;
export const ORPHANS_MIN_TERMINALS = 8;
export const NINE_GATES_MIN_SUIT_TILES = 8;

const TILE_TYPES = 34;
const SUIT_OFFSET = { m: 0, p: 9, s: 18, z: 27 } as const;
const SUIT_NAME = { m: 'Characters', p: 'Dots', s: 'Bamboo' } as const;
const NUMBERED = ['m', 'p', 's'] as const;
const DRAGON_NAME = ['White Dragon', 'Green Dragon', 'Red Dragon'];

const indexOf = (t: Tile): number => SUIT_OFFSET[suitOf(t)] + rankOf(t) - 1;
const tileAt = (i: number): Tile => (i < 27 ? `${(i % 9) + 1}${NUMBERED[Math.floor(i / 9)]}` : `${i - 26}z`);
const isTerminalIdx = (i: number): boolean => i < 27 && (i % 9 === 0 || i % 9 === 8);
const WINDS = [27, 28, 29, 30];
const DRAGONS = [31, 32, 33];
const ORPHANS = [...[0, 9, 18].flatMap((o) => [o, o + 8]), ...WINDS, ...DRAGONS];

/** Counts of what is held, for the "is it worth mentioning" rules. Declared kongs count as four tiles of their type. */
interface Stats {
  /** Pairs, counting four identical tiles as two. */
  pairs: number;
  /** Tile types held at least twice, plus declared kongs: a pair or a pung each. */
  groups: number;
  /** Tiles held per numbered suit (m, p, s). */
  suit: [number, number, number];
  honours: number;
  /** Per dragon: a pair of it is held (or a kong of it declared). */
  dragonPair: [boolean, boolean, boolean];
  /** Per dragon: a pung of it is held (or a kong of it declared). */
  dragonPung: [boolean, boolean, boolean];
  /** Most runs that can be made from different tiles: a full sequence, or two neighbouring tiles of one suit. */
  runs: number;
  /** Tiles that are 1s, 9s or honours. */
  edgeTiles: number;
  /** Tiles that are 1s or 9s. */
  terminalTiles: number;
  /** Wind tiles. */
  windTiles: number;
  /** Different terminals and honours held, out of the 13. */
  orphanTypes: number;
  /** Per suit: the Nine Gates start — 8+ tiles of it, a pair of one terminal and a tile of the other. */
  nineGatesSuit: [boolean, boolean, boolean];
  /** The longest run of consecutive ranks of one suit that are each held at least twice. */
  pairRun: number;
  kongCount: number;
}

function statsOf(held: number[], kongs: HeldKong[]): Stats {
  const kongTypes = kongs.map((k) => indexOf(k.tile));
  const suit: [number, number, number] = [0, 0, 0];
  let honours = 0;
  let pairs = 0;
  let groups = kongTypes.length;
  held.forEach((n, i) => {
    if (i < 27) suit[Math.floor(i / 9)]! += n;
    else honours += n;
    pairs += Math.floor(n / 2);
    if (n >= 2) groups++;
  });
  for (const i of kongTypes) {
    if (i < 27) suit[Math.floor(i / 9)]! += 4;
    else honours += 4;
  }
  const dragonPair = DRAGONS.map((d) => held[d]! >= 2 || kongTypes.includes(d)) as [boolean, boolean, boolean];
  const dragonPung = DRAGONS.map((d) => held[d]! >= 3 || kongTypes.includes(d)) as [boolean, boolean, boolean];
  const withKongs = (pick: (i: number) => boolean): number =>
    held.reduce((n, c, i) => n + (pick(i) ? c : 0), 0) + kongTypes.reduce((n, i) => n + (pick(i) ? 4 : 0), 0);
  // Runs: left to right, each tile pairs with its upper neighbour as far as the copies go (optimal on a line of ranks).
  let runs = 0;
  let pairRun = 0;
  for (let base = 0; base < 27; base += 9) {
    const rem = held.slice(base, base + 9);
    let streak = 0;
    for (let r = 0; r < 9; r++) {
      if (r < 8) {
        const k = Math.min(rem[r]!, rem[r + 1]!);
        runs += k;
        rem[r + 1]! -= k;
      }
      streak = held[base + r]! >= 2 ? streak + 1 : 0;
      pairRun = Math.max(pairRun, streak);
    }
  }
  return {
    pairs, groups, suit, honours, dragonPair, dragonPung, runs, pairRun,
    edgeTiles: withKongs((i) => isTerminalIdx(i) || i >= 27),
    terminalTiles: withKongs(isTerminalIdx),
    windTiles: withKongs((i) => i >= 27 && i <= 30),
    orphanTypes: ORPHANS.filter((i) => held[i]! >= 1).length,
    kongCount: kongs.length,
    nineGatesSuit: [0, 1, 2].map((n) => {
      const one = held[n * 9]!;
      const nine = held[n * 9 + 8]!;
      return suit[n]! >= NINE_GATES_MIN_SUIT_TILES && ((one >= 2 && nine >= 1) || (nine >= 2 && one >= 1));
    }) as [boolean, boolean, boolean],
  };
}

interface Fit {
  /** Held tiles that fit the best target hand for this pattern. */
  matched: number;
  /** Which held tiles those are, per tile type. */
  used: number[];
}

interface Spec {
  allow: (i: number) => boolean;
  chows: boolean;
  pungs: boolean;
  /** Tile types that must each be a pung/kong in the target. */
  forcedPungs?: number[];
  /** A tile type that must be the pair in the target. */
  forcedPair?: number;
}

/**
 * The most held tiles that fit ONE complete target (4 sets and a pair) of the shape `spec` describes. A set can be complete (3 tiles),
 * half of one (2), or a lone seed (1); the pair likewise (2 or 1). Everything a choice touches is within three tile types of where it
 * starts, so the search is memoised on (position, slots left, the next three counts).
 */
function fit(held: number[], kongs: HeldKong[], spec: Spec): Fit | undefined {
  const rem = held.map((n, i) => (spec.allow(i) ? n : 0));
  const used = new Array<number>(TILE_TYPES).fill(0);
  let melds = 4;
  let matched = 0;
  let pairFree = true;

  // A declared kong is a finished set of tiles that must be a pung of an allowed tile.
  const forced = [...(spec.forcedPungs ?? [])];
  for (const k of kongs) {
    const i = indexOf(k.tile);
    if (!spec.pungs || !spec.allow(i)) return undefined;
    melds--;
    matched += 3;
    const f = forced.indexOf(i);
    if (f >= 0) forced.splice(f, 1);
  }
  if (melds < 0) return undefined;
  for (const i of forced) {
    if (!spec.allow(i)) return undefined;
    const take = Math.min(rem[i]!, 3);
    rem[i]! -= take;
    used[i]! += take;
    matched += take;
    melds--;
  }
  if (melds < 0) return undefined;
  if (spec.forcedPair !== undefined) {
    const i = spec.forcedPair;
    if (!spec.allow(i)) return undefined;
    const take = Math.min(rem[i]!, 2);
    rem[i]! -= take;
    used[i]! += take;
    matched += take;
    pairFree = false;
  }

  const memo = new Map<number, number>();
  const base = (i: number): number => (i < TILE_TYPES ? rem[i]! : 0);

  const value = (i: number, m: number, p: boolean, a: number, b: number, c: number): number => {
    if (i >= TILE_TYPES || (m === 0 && !p)) return 0;
    const key = ((((i * 5 + m) * 2 + (p ? 1 : 0)) * 5 + Math.min(a, 4)) * 5 + Math.min(b, 4)) * 5 + Math.min(c, 4);
    const known = memo.get(key);
    if (known !== undefined) return known;
    let best = value(i + 1, m, p, b, c, base(i + 3)); // finished with this tile type: the rest of its copies are loose
    if (a > 0) {
      const r = i % 9;
      const inSuit = i < 27;
      if (spec.pungs && a >= 3 && m > 0) best = Math.max(best, 3 + value(i, m - 1, p, a - 3, b, c));
      if (spec.chows && inSuit && r <= 6 && b > 0 && c > 0 && m > 0) best = Math.max(best, 3 + value(i, m - 1, p, a - 1, b - 1, c - 1));
      if (p && a >= 2) best = Math.max(best, 2 + value(i, m, false, a - 2, b, c));
      if (spec.pungs && a >= 2 && m > 0) best = Math.max(best, 2 + value(i, m - 1, p, a - 2, b, c));
      if (spec.chows && inSuit && r <= 7 && b > 0 && m > 0) best = Math.max(best, 2 + value(i, m - 1, p, a - 1, b - 1, c));
      if (spec.chows && inSuit && r <= 6 && c > 0 && m > 0) best = Math.max(best, 2 + value(i, m - 1, p, a - 1, b, c - 1));
      if (m > 0 && (spec.pungs || (spec.chows && inSuit))) best = Math.max(best, 1 + value(i, m - 1, p, a - 1, b, c));
      if (p) best = Math.max(best, 1 + value(i, m, false, a - 1, b, c));
    }
    memo.set(key, best);
    return best;
  };

  const total = value(0, melds, pairFree, base(0), base(1), base(2));

  // Walk the same choices again, this time noting which tiles were used (so a suggestion can show "keep these").
  let i = 0;
  let m = melds;
  let p = pairFree;
  const cur = [base(0), base(1), base(2)] as [number, number, number];
  const take = (n0: number, n1 = 0, n2 = 0): void => {
    used[i]! += n0;
    if (n1) used[i + 1]! += n1;
    if (n2) used[i + 2]! += n2;
    cur[0] -= n0;
    cur[1] -= n1;
    cur[2] -= n2;
  };
  let guard = 0;
  while (i < TILE_TYPES && (m > 0 || p) && guard++ < 400) {
    const [a, b, c] = cur;
    const here = value(i, m, p, a, b, c);
    if (here === 0) break;
    if (here === value(i + 1, m, p, b, c, base(i + 3))) {
      i++;
      cur[0] = b;
      cur[1] = c;
      cur[2] = base(i + 2);
      continue;
    }
    const r = i % 9;
    const inSuit = i < 27;
    const tryOpt = (ok: boolean, gain: number, nm: number, np: boolean, d: [number, number, number]): boolean => {
      if (!ok || here !== gain + value(i, nm, np, a - d[0], b - d[1], c - d[2])) return false;
      take(d[0], d[1], d[2]);
      m = nm;
      p = np;
      return true;
    };
    if (a <= 0) break;
    if (
      tryOpt(spec.pungs && a >= 3 && m > 0, 3, m - 1, p, [3, 0, 0]) ||
      tryOpt(spec.chows && inSuit && r <= 6 && b > 0 && c > 0 && m > 0, 3, m - 1, p, [1, 1, 1]) ||
      tryOpt(p && a >= 2, 2, m, false, [2, 0, 0]) ||
      tryOpt(spec.pungs && a >= 2 && m > 0, 2, m - 1, p, [2, 0, 0]) ||
      tryOpt(spec.chows && inSuit && r <= 7 && b > 0 && m > 0, 2, m - 1, p, [1, 1, 0]) ||
      tryOpt(spec.chows && inSuit && r <= 6 && c > 0 && m > 0, 2, m - 1, p, [1, 0, 1]) ||
      tryOpt(m > 0 && (spec.pungs || (spec.chows && inSuit)), 1, m - 1, p, [1, 0, 0]) ||
      tryOpt(p, 1, m, false, [1, 0, 0])
    ) {
      continue;
    }
    break;
  }
  return { matched: matched + total, used };
}

const keepTiles = (used: number[]): Tile[] => used.flatMap((n, i) => Array<Tile>(n).fill(tileAt(i)));

// ---- patterns that are not "4 sets and a pair" ------------------------------------------------------------------------------------

/** Seven pairs — four identical tiles count as two of them: take the seven best pair slots. */
function sevenPairs(held: number[], kongs: HeldKong[]): Fit | undefined {
  if (kongs.length > 0) return undefined;
  const slots: { i: number; take: number }[] = [];
  held.forEach((n, i) => {
    if (n >= 1) slots.push({ i, take: Math.min(n, 2) });
    if (n >= 3) slots.push({ i, take: Math.min(n - 2, 2) });
  });
  slots.sort((x, y) => y.take - x.take);
  const used = new Array<number>(TILE_TYPES).fill(0);
  let matched = 0;
  for (const slot of slots.slice(0, 7)) {
    used[slot.i]! += slot.take;
    matched += slot.take;
  }
  return { matched, used };
}

/** One of each of the 13 terminals and honours, plus one more of any of them as the pair. */
function thirteenOrphans(held: number[], kongs: HeldKong[]): Fit | undefined {
  if (kongs.length > 0) return undefined;
  const used = new Array<number>(TILE_TYPES).fill(0);
  let matched = 0;
  let spare = false;
  for (const i of ORPHANS) {
    if (held[i]! >= 1) {
      used[i] = 1;
      matched++;
      if (held[i]! >= 2) spare = true;
    }
  }
  if (spare) {
    const i = ORPHANS.find((o) => held[o]! >= 2)!;
    used[i] = 2;
    matched++;
  }
  return { matched, used };
}

/** Seven pairs of consecutive ranks in one suit (1-7, 2-8 or 3-9). */
function sevenShiftedPairs(held: number[], kongs: HeldKong[]): Fit | undefined {
  if (kongs.length > 0) return undefined;
  let best: Fit | undefined;
  for (const s of NUMBERED) {
    for (let start = 0; start <= 2; start++) {
      const used = new Array<number>(TILE_TYPES).fill(0);
      let matched = 0;
      for (let r = start; r < start + 7; r++) {
        const i = SUIT_OFFSET[s] + r;
        used[i] = Math.min(held[i]!, 2);
        matched += used[i]!;
      }
      if (!best || matched > best.matched) best = { matched, used };
    }
  }
  return best;
}

/** Four kongs and a pair: 18 tiles in all, so the "14" the others count against is 18 here. */
function allKongs(held: number[], kongs: HeldKong[]): Fit & { total: number } {
  const used = new Array<number>(TILE_TYPES).fill(0);
  const kongTypes = new Set(kongs.map((k) => indexOf(k.tile)));
  let matched = kongs.length * 4;
  const free = held.map((n, i) => ({ i, n })).filter((x) => !kongTypes.has(x.i) && x.n > 0).sort((a, b) => b.n - a.n);
  const sets = free.slice(0, 4 - kongs.length);
  for (const x of sets) {
    used[x.i] = Math.min(x.n, 4);
    matched += used[x.i]!;
  }
  const pair = free.slice(4 - kongs.length)[0];
  if (pair) {
    used[pair.i] = Math.min(pair.n, 2);
    matched += used[pair.i]!;
  }
  return { matched, used, total: 18 };
}

/** One suit as 1112345678999 plus any one more tile of that suit. */
function nineGates(held: number[], kongs: HeldKong[], suits: readonly boolean[]): Fit | undefined {
  if (kongs.length > 0) return undefined;
  const REQUIRED = [3, 1, 1, 1, 1, 1, 1, 1, 3];
  let best: Fit | undefined;
  for (const s of NUMBERED) {
    if (!suits[NUMBERED.indexOf(s)]) continue; // only a suit that already has the start of it
    const used = new Array<number>(TILE_TYPES).fill(0);
    let matched = 0;
    let spare: number | undefined;
    for (let r = 0; r < 9; r++) {
      const i = SUIT_OFFSET[s] + r;
      used[i] = Math.min(held[i]!, REQUIRED[r]!);
      matched += used[i]!;
      if (held[i]! > REQUIRED[r]! && spare === undefined) spare = i;
    }
    if (spare !== undefined) {
      used[spare]!++;
      matched++;
    }
    if (!best || matched > best.matched) best = { matched, used };
  }
  return best;
}

// ---- the catalogue ----------------------------------------------------------------------------------------------------------------

interface Entry {
  key: string;
  label: string;
  fan: number;
  limit: boolean;
  /** The best fit over the variants this pattern has (a suit, which dragon ...), and a short phrase saying what it is. */
  fit(held: number[], kongs: HeldKong[], st: Stats): { fit: Fit; note: string; total?: number } | undefined;
}

function best(variants: { fit: Fit | undefined; note: string }[]): { fit: Fit; note: string } | undefined {
  let top: { fit: Fit; note: string } | undefined;
  for (const v of variants) {
    if (v.fit && (!top || v.fit.matched > top.fit.matched)) top = { fit: v.fit, note: v.note };
  }
  return top;
}

const allowAll = (): boolean => true;

/** A pung of one tile type AND a pair of a different one (an honour group: dragons or winds). */
function pungAndAnotherPair(pung: boolean[], pair: boolean[]): boolean {
  return pung.some((p, i) => p && pair.some((q, j) => q && j !== i));
}

function catalogue(settings: HkosSettings): Entry[] {
  const cap = activeFanCap(settings);
  const f = settings.fan;
  const one = (fitFn: (held: number[], kongs: HeldKong[]) => Fit | undefined, note: (fit: Fit) => string) => (held: number[], kongs: HeldKong[]) => {
    const r = fitFn(held, kongs);
    return r ? { fit: r, note: note(r) } : undefined;
  };
  const spec = (s: Spec, note: string) => (held: number[], kongs: HeldKong[]) => {
    const r = fit(held, kongs, s);
    return r ? { fit: r, note } : undefined;
  };

  return [
    {
      key: 'allSequences', label: 'All Sequences', fan: f.allSequences, limit: false,
      fit: (held, kongs, st) => (st.runs >= ALL_SEQUENCES_MIN_RUNS ? spec({ allow: allowAll, chows: true, pungs: false }, 'every set a sequence — no pungs')(held, kongs) : undefined),
    },
    {
      key: 'allPungs', label: 'All Pungs', fan: f.allPungs, limit: false,
      fit: (held, kongs, st) => (st.groups >= ALL_PUNGS_MIN_GROUPS ? spec({ allow: allowAll, chows: false, pungs: true }, 'every set a pung')(held, kongs) : undefined),
    },
    { key: 'sevenPairs', label: 'Seven Pairs', fan: f.sevenPairs, limit: false, fit: (held, kongs, st) => { if (st.pairs < SEVEN_PAIRS_MIN_PAIRS) return undefined; const r = sevenPairs(held, kongs); return r ? { fit: r, note: `seven pairs — you have ${st.pairs}` } : undefined; } },
    {
      key: 'halfFlush', label: 'Half Flush', fan: f.halfFlush, limit: false,
      fit: (held, kongs, st) => best(NUMBERED.map((s, n) => ({ fit: st.suit[n]! >= HALF_FLUSH_MIN_SUIT_TILES && st.honours >= HALF_FLUSH_MIN_HONOURS ? fit(held, kongs, { allow: (i) => i >= 27 || Math.floor(i / 9) === n, chows: true, pungs: true }) : undefined, note: `${SUIT_NAME[s]} and honour tiles only` }))),
    },
    {
      key: 'fullFlush', label: 'Full Flush', fan: f.fullFlush, limit: false,
      fit: (held, kongs, st) => best(NUMBERED.map((s, n) => ({ fit: st.suit[n]! >= FULL_FLUSH_MIN_SUIT_TILES && st.honours === 0 ? fit(held, kongs, { allow: (i) => i < 27 && Math.floor(i / 9) === n, chows: true, pungs: true }) : undefined, note: `${SUIT_NAME[s]} only` }))),
    },
    {
      key: 'mixedOrphans', label: 'Mixed Orphans', fan: f.mixedOrphans, limit: false,
      fit: (held, kongs, st) => (st.edgeTiles >= MIXED_ORPHANS_MIN_TILES ? spec({ allow: (i) => isTerminalIdx(i) || i >= 27, chows: false, pungs: true }, `only 1s, 9s and honours (+${f.allPungs} with All Pungs)`)(held, kongs) : undefined),
    },
    {
      key: 'dragonPung', label: 'Dragon Pung', fan: f.dragonPung, limit: false,
      fit: (held, kongs, st) => best(DRAGONS.map((d, n) => ({ fit: st.dragonPair[n] ? fit(held, kongs, { allow: allowAll, chows: true, pungs: true, forcedPungs: [d] }) : undefined, note: `a pung of ${DRAGON_NAME[n]}` }))),
    },
    {
      key: 'smallDragons', label: 'Small Dragons', fan: f.smallDragons, limit: false,
      fit: (held, kongs, st) => (st.dragonPair.filter(Boolean).length >= 2 ? best(DRAGONS.map((d) => ({ fit: fit(held, kongs, { allow: allowAll, chows: true, pungs: true, forcedPungs: DRAGONS.filter((x) => x !== d), forcedPair: d }), note: 'two dragon pungs and a pair of the third' }))) : undefined),
    },
    {
      key: 'greatDragons', label: 'Great Dragons', fan: f.greatDragons, limit: false,
      fit: (held, kongs, st) => (pungAndAnotherPair(st.dragonPung, st.dragonPair) ? spec({ allow: allowAll, chows: true, pungs: true, forcedPungs: DRAGONS }, 'a pung of every dragon')(held, kongs) : undefined),
    },
    {
      key: 'smallWinds', label: 'Small Winds', fan: f.smallWinds, limit: false,
      fit: (held, kongs, st) => (st.windTiles >= SMALL_WINDS_MIN_WIND_TILES ? best(WINDS.map((w) => ({ fit: fit(held, kongs, { allow: allowAll, chows: true, pungs: true, forcedPungs: WINDS.filter((x) => x !== w), forcedPair: w }), note: 'three wind pungs and a pair of the fourth' }))) : undefined),
    },
    // ---- limit hands ("Want to Win Big?")
    { key: 'thirteenOrphans', label: 'Thirteen Orphans', fan: cap, limit: true, fit: (held, kongs, st) => (st.orphanTypes >= THIRTEEN_ORPHANS_MIN_TYPES ? one(thirteenOrphans, () => 'one of each 1, 9 and honour, plus a pair of any')(held, kongs) : undefined) },
    { key: 'sevenShiftedPairs', label: 'Seven Shifted Pairs', fan: cap, limit: true, fit: (held, kongs, st) => (st.pairRun >= SEVEN_SHIFTED_MIN_RUN ? one(sevenShiftedPairs, () => 'seven pairs in one suit, in a run (like 1 to 7)')(held, kongs) : undefined) },
    {
      key: 'greatWinds', label: 'Great Winds', fan: cap, limit: true,
      fit: (held, kongs, st) => (st.windTiles >= GREAT_WINDS_MIN_WIND_TILES ? spec({ allow: allowAll, chows: true, pungs: true, forcedPungs: WINDS }, 'a pung of every wind')(held, kongs) : undefined),
    },
    {
      key: 'allKongs', label: 'All Kongs', fan: cap, limit: true,
      fit: (held, kongs, st) => { if (st.kongCount < ALL_KONGS_MIN_KONGS) return undefined; const r = allKongs(held, kongs); return { fit: r, note: 'four kongs and a pair', total: r.total }; },
    },
    {
      key: 'allHonours', label: 'All Honours', fan: fixedLimitFan(settings, 'allHonours'), limit: true,
      fit: (held, kongs, st) => (st.honours >= ALL_HONOURS_MIN_TILES ? spec({ allow: (i) => i >= 27, chows: false, pungs: true }, 'winds and dragons only')(held, kongs) : undefined),
    },
    {
      key: 'orphans', label: 'Orphans', fan: fixedLimitFan(settings, 'orphans'), limit: true,
      fit: (held, kongs, st) => (st.terminalTiles >= ORPHANS_MIN_TERMINALS ? spec({ allow: (i) => isTerminalIdx(i), chows: false, pungs: true }, 'only 1s and 9s')(held, kongs) : undefined),
    },
    { key: 'nineGates', label: 'Nine Gates', fan: fixedLimitFan(settings, 'nineGates'), limit: true, fit: (held, kongs, st) => (st.nineGatesSuit.some(Boolean) ? one((h, k) => nineGates(h, k, st.nineGatesSuit), () => 'one suit: 1112345678999 plus any tile of it')(held, kongs) : undefined) },
    {
      key: 'selfTriplets', label: 'Self Triplets', fan: fixedLimitFan(settings, 'selfTriplets'), limit: true,
      fit: (held, kongs, st) => (kongs.some((k) => !k.concealed) || st.groups < ALL_PUNGS_MIN_GROUPS ? undefined : (spec({ allow: allowAll, chows: false, pungs: true }, 'four pungs and a pair, all concealed — no claims')(held, kongs))),
    },
  ];
}

/**
 * The patterns a hand is closest to, or undefined when there is nothing to say yet (fewer than 8 tiles entered) or no longer (the hand is
 * already complete — the preview takes over then). `concealed` is what is in hand; a declared kong counts as a finished set.
 */
export function suggestPatterns(concealed: Tile[], kongs: HeldKong[], settings: HkosSettings): Suggestions | undefined {
  const entered = concealed.length + kongs.length * 4;
  if (entered < SUGGEST_MIN_TILES || concealed.length + kongs.length * 3 >= 14) return undefined;

  const held = new Array<number>(TILE_TYPES).fill(0);
  for (const t of concealed) held[indexOf(t)]! += 1;

  const stats = statsOf(held, kongs);
  const regular: Suggestion[] = [];
  const big: Suggestion[] = [];
  for (const entry of catalogue(settings)) {
    const r = entry.fit(held, kongs, stats);
    if (!r) continue;
    const total = r.total ?? 14;
    const need = total - r.fit.matched;
    if (need < 1 || need > (entry.limit ? SUGGEST_BIG_WITHIN : SUGGEST_REGULAR_WITHIN)) continue;
    (entry.limit ? big : regular).push({ key: entry.key, label: entry.label, fan: entry.fan, need, note: r.note, keep: keepTiles(r.fit.used), limit: entry.limit });
  }
  // Fewest tiles first; for the same number, the smaller-point pattern first — it is the likelier to come in.
  const order = (a: Suggestion, b: Suggestion): number => a.need - b.need || a.fan - b.fan || a.label.localeCompare(b.label);
  regular.sort(order);
  big.sort(order);
  return { entered, regular: regular.slice(0, SUGGEST_MAX_REGULAR), big: big.slice(0, SUGGEST_MAX_BIG) };
}
